import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight, X } from 'lucide-react';

interface Props {
  pendingCount: number;
  completedCount: number;
  totalItems: number;
  onDismiss: () => void;
}

export default function HomeWelcomeBanner({ pendingCount, completedCount, totalItems, onDismiss }: Props) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  function dismiss() {
    setDismissed(true);
    onDismiss();
  }

  const pct = totalItems > 0 ? Math.round((completedCount / totalItems) * 100) : 0;

  return (
    <AnimatePresence>
      {!dismissed && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8, height: 0, marginBottom: 0 }}
          transition={{ duration: 0.25 }}
          className="rounded-2xl border border-ui-accent/30 bg-ui-accent/8 p-4 flex items-start gap-4"
        >
          {/* Icono */}
          <div className="flex-shrink-0 mt-0.5 flex h-9 w-9 items-center justify-center rounded-xl bg-ui-accent/15">
            <Sparkles className="h-5 w-5 text-ui-accent" />
          </div>

          {/* Contenido */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-ui-text">Tu sitio está recién listo</p>
            <p className="text-sm text-ui-text-muted mt-0.5 leading-snug">
              La IA generó el contenido de arranque. Revisa cada sección para reemplazar
              los datos de muestra con los de tu negocio.
            </p>

            {/* Barra de progreso */}
            <div className="mt-3 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-sm text-ui-text-subtle">
                  {completedCount} de {totalItems} tareas completadas
                </span>
                <span className="text-sm font-medium text-ui-accent">{pct}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-ui-text/10 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                  className="h-full rounded-full bg-ui-accent"
                />
              </div>
            </div>

            {pendingCount > 0 && (
              <button
                type="button"
                onClick={() => navigate('/getting-started')}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-ui-accent hover:opacity-80 transition-opacity"
              >
                Ver las {pendingCount} tareas pendientes <ArrowRight className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Cerrar */}
          <button
            type="button"
            onClick={dismiss}
            className="flex-shrink-0 p-1 rounded-lg text-ui-text-subtle hover:text-ui-text hover:bg-ui-text/5 transition-colors"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
